/* global VERSION */
import { toastr } from 'react-redux-toastr';
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import i18next from 'i18next';
import { jwtDecode } from 'jwt-decode';

import { urls } from './urls';
import { store } from '../index';
import { logout, tokenRefreshSuccess } from '@/actions/authActions';
import { syncOpenFeatureContext } from './openFeature';
import { hasLiveRefreshToken } from './session';
import { VERSION } from 'underscore';

const { server } = urls;

export const instance = axios.create({
  baseURL: server.url,
  headers: { 'X-Frontend-Version': VERSION },
});

interface ErrorResponse {
  description: string;
}

type RetryableConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
};

let refreshInflight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = sessionStorage.getItem('refresh');
  if (!refreshToken) throw new Error('no refresh token');

  // Bypass the response interceptor's refresh logic for this call by
  // tagging the request config — see RetryableConfig._skipAuthRefresh.
  let data;
  try {
    ({ data } = await instance.post(
      server.auth.refresh,
      { refresh_token: refreshToken },
      { _skipAuthRefresh: true } as AxiosRequestConfig,
    ));
  } catch (error) {
    // Only the server refusing the refresh token ends the session. A network error
    // or a 5xx leaves the token in place, so the next request can try again.
    const status = (error as AxiosError).response?.status;
    if (status === 400 || status === 401 || status === 403) {
      sessionStorage.removeItem('refresh');
    }
    throw error;
  }

  // The refresh endpoint isn't in the OpenAPI spec and isn't guaranteed to use
  // the same envelope as login (`{ token: { access, refresh } }`). Accept both
  // that nested shape and the flat `{ access_token }` shape, and fail loudly
  // rather than store an `undefined` token that would 401 every later request.
  const access: string | undefined = data?.access_token ?? data?.token?.access;
  if (!access) throw new Error('refresh response missing access token');

  sessionStorage.setItem('token', access);

  // If the backend rotates refresh tokens, persist the new one — otherwise the
  // next refresh would reuse a now-invalid token and force a logout.
  const rotatedRefresh: string | undefined = data?.refresh_token ?? data?.token?.refresh;
  if (rotatedRefresh) sessionStorage.setItem('refresh', rotatedRefresh);

  let tokenExpiry = 0;
  try {
    const decoded: { exp: number } = jwtDecode(access);
    tokenExpiry = decoded.exp * 1000;
    sessionStorage.setItem('expiring', tokenExpiry.toString());
  } catch { }
  instance.defaults.headers.common.Authorization = `JWT ${access}`;
  store.dispatch(tokenRefreshSuccess(access, tokenExpiry));
  syncOpenFeatureContext();

  // Broadcast so an already-mounted micro-frontend updates its token without a
  // remount. Every refresh funnels through here — the host's own 401 retry as
  // well as the MFE-initiated handshake — so this is the single point that
  // keeps the MFE's token in sync.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mfe:token-refreshed', { detail: { token: access } }));
  }

  return access;
}

export function refreshAccessToken(): Promise<string> {
  if (!refreshInflight) {
    refreshInflight = performRefresh().finally(() => {
      refreshInflight = null;
    });
  }
  return refreshInflight;
}

async function globalErrorHandler(error: AxiosError): Promise<unknown> {
  const original = error.config as RetryableConfig | undefined;

  if (
    error.response?.status === 401 &&
    original &&
    !original._retry &&
    !original._skipAuthRefresh &&
    sessionStorage.getItem('refresh')
  ) {
    original._retry = true;
    try {
      const newToken = await refreshAccessToken();
      original.headers.set?.('Authorization', `JWT ${newToken}`);
      return instance.request(original);
    } catch {
      if (!hasLiveRefreshToken()) store.dispatch(logout());
      return Promise.reject(error);
    }
  }

  if (error.response) {
    if (error.response.status === 403) {
      const { description } = (error.response.data as ErrorResponse) || {
        description: 'Diese Funktion ist nur in der Vollversion von Smartlane verfügbar.',
      };
      toastr.error(i18next.t('common:states.access'), description);
    } else if (error.response.status === 401 && !original?._skipAuthRefresh) {
      // Auth calls (refresh, logout) handle their own 401s: refresh's caller
      // already logs out, and logging out on a failed logout would loop.
      store.dispatch(logout());
    }
  }
  return Promise.reject(error);
}

instance.defaults.headers.post['Content-Type'] = 'application/json';
instance.defaults.headers.patch['Content-Type'] = 'application/json';

instance.interceptors.response.use(
  (response) => response,
  globalErrorHandler
);

instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { url } = config;

    if (indestructibleRequest(url)) {
      return config;
    } else {
      return { ...config };
    }
  },
  (error: AxiosError) => Promise.reject(error)
);

if (sessionStorage.token) {
  instance.defaults.headers.common.Authorization = `JWT ${sessionStorage.token}`;
}

const permitted: RegExp[] = [
  new RegExp(server.auth.login, 'i'),
  new RegExp(server.auth.user_profile, 'i'),
  new RegExp(server.auth.reset, 'i'),
  new RegExp(server.auth.activate, 'i'),
];

function indestructibleRequest(url: string): boolean {
  return permitted.some((exp) => exp.test(url));
}
